'use client';

import { useRouter } from 'next/navigation';
import { Box, Button, EditIcon, IconButton, Typography } from '@/lib/mui';
import UserPlaceholder from '@/components/icons/UserPlaceholder';
import Email from '@/components/icons/Email';
import EmptyPhone from '@/components/icons/EmptyPhone';
import { UserProfile } from '@/services/customerProfileService.types';
import ContactIcon from './_icon';
import Section from '../Section';

interface Props {
  user?: UserProfile;
}

export default function ContactInformation({ user }: Props) {
  const router = useRouter();

  return (
    <Section
      Icon={ContactIcon}
      title="Contact Information"
      desc="Your contact information will be used to send you delivery updates and apply your preferences to future orders and returns."
    >
      <Box mt={4} display="flex">
        <Box margin="auto">
          <UserPlaceholder s={{ height: 40, width: 40 }} />
        </Box>
        <Box ml={2} flex={1}>
          <Typography variant="h6">Name</Typography>
          {user?.first_name ? (
            <Typography variant="body1">
              {user?.first_name} {user?.last_name}
            </Typography>
          ) : (
            <Button
              variant="text"
              color="secondary"
              onClick={() => router.push('/name/edit')}
              style={{ padding: 0 }}
            >
              Add Name
            </Button>
          )}
        </Box>
        <Box margin="auto">
          <IconButton
            aria-label="delete"
            size="large"
            onClick={() => router.push('/name/edit')}
          >
            <EditIcon color="secondary" />
          </IconButton>
        </Box>
      </Box>
      <Box mt={4} display="flex">
        <Box margin="auto">
          <Email />
        </Box>
        <Box ml={2} sx={{ width: '100%', overflow: 'hidden' }}>
          <Typography variant="h6">Email</Typography>
          {user?.primary_email?.value ? (
            <Typography
              variant="body1"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {user.primary_email.value}
            </Typography>
          ) : (
            <Button
              variant="text"
              color="secondary"
              onClick={() => router.push('/setup-profile/email')}
              style={{ padding: 0 }}
            >
              Add Email Address
            </Button>
          )}
        </Box>
        <Box margin="auto">
          <IconButton
            aria-label="delete"
            size="large"
            onClick={() => router.push('/email/edit')}
          >
            <EditIcon color="secondary" />
          </IconButton>
        </Box>
      </Box>
      <Box mt={4} display="flex">
        <Box margin="auto">
          <EmptyPhone />
        </Box>
        <Box ml={2} flex={1}>
          <Typography variant="h6">Phone</Typography>
          {user?.primary_phone_number?.value ? (
            <Typography variant="body1">
              {user.primary_phone_number.value}
            </Typography>
          ) : (
            <Button
              variant="text"
              color="secondary"
              onClick={() => router.push('/setup-profile/phone')}
              style={{ padding: 0 }}
            >
              Add Phone Number
            </Button>
          )}
        </Box>
        <Box margin="auto">
          <IconButton
            aria-label="delete"
            size="large"
            onClick={() => router.push('/phone/edit')}
          >
            <EditIcon color="secondary" />
          </IconButton>
        </Box>
      </Box>
    </Section>
  );
}
